// The Studio's task list: a sheet of runs, one line each, newest first,
// with the fraction that matters (done / requested) and a way straight
// into the gallery of any one of them.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listTasks, type TaskView } from "../api/client";
import { formatMinor, formatWhen } from "../lib/format";

const STATES = ["", "QUEUED", "RUNNING", "DONE", "FAILED", "CANCELLED"];

export default function Tasks() {
  const [tasks, setTasks] = useState<TaskView[]>([]);
  const [state, setState] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listTasks({ state: state || undefined, limit: 100 })
      .then((t) => !cancelled && setTasks(t))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : "failed to load tasks"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [state]);

  return (
    <div className="u-page">
      <div className="u-head">
        <h1 className="u-h1">Runs</h1>
        <p className="u-lede">Every batch you have started, with its results one click away.</p>
      </div>

      <div className="u-sheet__bar">
        <label className="u-field u-field--row">
          Showing
          <select className="u-select" value={state} onChange={(e) => setState(e.target.value)}>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s || "all states"}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="u-error">{error}</p>}
      {loading ? (
        <p className="u-loading">Loading…</p>
      ) : tasks.length === 0 ? (
        <p className="u-empty">
          Nothing here yet. <Link to="/">Start a run</Link>.
        </p>
      ) : (
        <table className="u-table">
          <thead>
            <tr>
              <th>Run</th>
              <th>Model</th>
              <th>State</th>
              <th>Results</th>
              <th>Quoted</th>
              <th>Started</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              // The whole row opens the run; the id is its link for the
              // keyboard and for "open in new tab".
              <tr key={t.task_id} className="u-row-link" onClick={() => navigate(`/tasks/${t.task_id}`)}>
                <td>
                  <Link className="u-runlink" to={`/tasks/${t.task_id}`} onClick={(e) => e.stopPropagation()}>
                    {t.task_id.slice(0, 8)}
                    <span aria-hidden="true"> →</span>
                  </Link>
                </td>
                <td>{t.alias}</td>
                <td>
                  <span className={`u-state u-state--${t.state.toLowerCase()}`}>{t.state.toLowerCase()}</span>
                </td>
                <td className="u-num">
                  {t.done} / {t.requested}
                  {t.failed > 0 && <span className="u-state u-state--failed"> {t.failed} failed</span>}
                </td>
                <td className="u-num">{formatMinor(t.quoted_total, t.currency)}</td>
                <td>{formatWhen(t.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
